class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 450);

    const facetForm = this.querySelector('form');
    if (facetForm && !facetForm.hasAttribute('data-deferred-facets')) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const targetHistoryIndex = Number.isInteger(event.state?.facetHistoryIndex)
        ? event.state.facetHistoryIndex
        : null;
      if (FacetFiltersForm.restoringHistory) {
        FacetFiltersForm.restoringHistory = false;
        if (targetHistoryIndex !== null) FacetFiltersForm.historyIndex = targetHistoryIndex;
        return;
      }
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false, targetHistoryIndex);
    };
    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-facet-retry]') || FacetFiltersForm.failedRequest === null) return;
      event.preventDefault();
      const failedRequest = FacetFiltersForm.failedRequest;
      if (
        failedRequest.updateURLHash === false &&
        Number.isInteger(failedRequest.targetHistoryIndex) &&
        failedRequest.targetHistoryIndex !== FacetFiltersForm.historyIndex
      ) {
        history.go(failedRequest.targetHistoryIndex - FacetFiltersForm.historyIndex);
        return;
      }
      FacetFiltersForm.renderPage(
        failedRequest.searchParams,
        null,
        failedRequest.updateURLHash,
        failedRequest.targetHistoryIndex
      );
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, targetHistoryIndex = null) {
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();
    const signal = FacetFiltersForm.abortController.signal;
    const request = { id: ++FacetFiltersForm.requestId, searchParams, updateURLHash, targetHistoryIndex };
    FacetFiltersForm.activeRequest = request;
    FacetFiltersForm.hideError();
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGrid = document.getElementById('ProductGridContainer');
    const collection = productGrid ? productGrid.querySelector('.collection') : null;
    if (productGrid) productGrid.setAttribute('aria-busy', 'true');
    FacetFiltersForm.setApplyLoading(true);
    if (collection) collection.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, request)
        : FacetFiltersForm.renderSectionFromFetch(url, event, signal, request);
    });
  }

  static renderSectionFromFetch(url, event, signal, request) {
    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Filtre isteği başarısız: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (signal.aborted || FacetFiltersForm.activeRequest?.id !== request.id) return;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }].slice(-20);
        FacetFiltersForm.renderSection(responseText, event);
        FacetFiltersForm.commitRequest(request);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[facets] render error:', error);
        FacetFiltersForm.rollbackRequest(request);
      });
  }

  static clearLoadingState() {
    document.querySelector('#ProductGridContainer .collection')?.classList.remove('loading');
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((container) => {
      container.classList.remove('loading');
    });
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', 'false');
    FacetFiltersForm.setApplyLoading(false);
  }

  static renderSectionFromCache(filterDataUrl, event, request) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderSection(html, event);
      FacetFiltersForm.commitRequest(request);
    } catch (error) {
      console.error('[facets] cache render error:', error);
      FacetFiltersForm.rollbackRequest(request);
    }
  }

  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!parsedHTML.getElementById('ProductGridContainer') || !parsedHTML.getElementById('ProductCount')) {
      throw new Error('Filtre yaniti gerekli bolumleri icermiyor.');
    }
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid && typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(productGrid);
    }
    FacetFiltersForm.closeMobileDrawerAfterRender();
  }

  static commitRequest(request) {
    if (FacetFiltersForm.activeRequest?.id !== request.id) return;
    FacetFiltersForm.committedSearchParams = request.searchParams;
    FacetFiltersForm.searchParamsPrev = request.searchParams;
    FacetFiltersForm.failedRequest = null;
    FacetFiltersForm.activeRequest = null;
    FacetFiltersForm.commitFilterControls();
    FacetFiltersForm.hideError();
    FacetFiltersForm.clearLoadingState();
    if (request.updateURLHash && window.location.search.slice(1) !== request.searchParams) {
      FacetFiltersForm.updateURLHash(request.searchParams);
    } else if (!request.updateURLHash && Number.isInteger(request.targetHistoryIndex)) {
      FacetFiltersForm.historyIndex = request.targetHistoryIndex;
    }
  }

  static rollbackRequest(request) {
    if (FacetFiltersForm.activeRequest?.id !== request.id) return;
    FacetFiltersForm.failedRequest = {
      searchParams: request.searchParams,
      updateURLHash: request.updateURLHash,
      targetHistoryIndex: request.targetHistoryIndex,
    };
    FacetFiltersForm.searchParamsPrev = FacetFiltersForm.committedSearchParams;
    FacetFiltersForm.activeRequest = null;
    FacetFiltersForm.clearLoadingState();
    FacetFiltersForm.restoreFilterControls();
    if (
      request.updateURLHash === false &&
      Number.isInteger(request.targetHistoryIndex) &&
      request.targetHistoryIndex !== FacetFiltersForm.historyIndex
    ) {
      FacetFiltersForm.restoringHistory = true;
      history.go(FacetFiltersForm.historyIndex - request.targetHistoryIndex);
    }
    FacetFiltersForm.showError();
  }

  static commitFilterControls() {
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      form.querySelectorAll('input').forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') input.defaultChecked = input.checked;
        else input.defaultValue = input.value;
      });
      form.querySelectorAll('option').forEach((option) => {
        option.defaultSelected = option.selected;
      });
      form.querySelectorAll('textarea').forEach((textarea) => {
        textarea.defaultValue = textarea.value;
      });
    });
  }

  static restoreFilterControls() {
    document.querySelectorAll('facet-filters-form form').forEach((form) => form.reset());
    const controller = window.__bottomSheetController;
    if (controller) {
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) controller.syncSortSelection(mobileForm);
      controller.updateSelectionStyles();
      document.querySelectorAll('[data-price-range]').forEach((range) => controller.paintPriceTrack(range));
    }
    FacetFiltersForm.updateBottomSheetBadge();
  }

  static showError() {
    const error = document.getElementById('FacetFilterError');
    if (error) error.hidden = false;
  }

  static hideError() {
    const error = document.getElementById('FacetFilterError');
    if (error) error.hidden = true;
  }

  static setApplyLoading(isLoading) {
    const apply = document.querySelector('[data-bottomsheet-apply="filter-sort"]');
    if (!apply) return;
    apply.disabled = isLoading;
    const label = isLoading ? apply.dataset.loadingLabel : apply.dataset.defaultLabel;
    if (label) apply.textContent = label;
  }

  static renderProductGridContainer(html) {
    const productGrid = document.getElementById('ProductGridContainer');
    const incomingGrid = html.getElementById('ProductGridContainer');
    if (!productGrid || !incomingGrid) return;
    productGrid.innerHTML = incomingGrid.innerHTML;

    productGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Yeni eklenen tm-lazy görselleri hemen observe et
    if (window.tmLazy && window.tmLazy.initImageLazyLoad) {
      window.tmLazy.initImageLazyLoad();
    }

    // Re-relocate any sheets that came in via the new HTML (in case grid had its own copy)
    if (window.__bottomSheetController) {
      window.__bottomSheetController.relocateWrapper();
    }
  }

  static renderProductCount(html) {
    const incomingCount = html.getElementById('ProductCount');
    const incomingCountDesktop = html.getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!incomingCount || !container) {
      FacetFiltersForm.clearLoadingState();
      return;
    }
    container.innerHTML = incomingCount.innerHTML;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = (incomingCountDesktop || incomingCount).innerHTML;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    FacetFiltersForm.updateBottomSheetBadge();
    FacetFiltersForm.syncVisibleProductCount();
  }

  static syncVisibleProductCount() {
    const container = document.getElementById('ProductGridContainer');
    const grid = container?.querySelector('#product-grid.ty-product-grid');
    if (!grid) return;
    const count = grid.querySelectorAll(':scope > .ty-card').length;
    const template = count === 1 ? container.dataset.visibleCountOne : container.dataset.visibleCountOther;
    if (!template) return;
    const formattedCount = new Intl.NumberFormat(document.documentElement.lang || undefined).format(count);
    const label = template.replace('__COUNT__', formattedCount);
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((element) => {
      element.textContent = label;
    });
  }

  static renderFilters(html, event) {
    const facetDetailsElementsFromFetch = html.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(html);
    FacetFiltersForm.renderAdditionalElements(html);
    FacetFiltersForm.updateBottomSheetBadge();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const domEl = document.querySelector(selector);
      if (domEl) domEl.innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
        menuDrawer.bindEvents();
      }
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static closeMobileDrawerAfterRender() {
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (!mobileForm) return;

    const menuDrawer = mobileForm.closest('menu-drawer');
    if (menuDrawer) {
      const mainDetails = menuDrawer.querySelector('details');
      if (mainDetails && !mainDetails.classList.contains('menu-opening')) {
        document.body.classList.remove('overflow-hidden-mobile');
        document.body.style.overflow = '';
      }
    }

    const bottomSheet = mobileForm.closest('.bottomsheet');
    if (bottomSheet && bottomSheet.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  }

  static updateBottomSheetBadge() {
    const filterBtn = document.querySelector('[data-bottomsheet-target="filter-sort"]');
    if (!filterBtn) return;
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (!mobileForm) return;
    let activeCount = mobileForm.querySelectorAll('input[type="checkbox"]:checked').length;
    mobileForm.querySelectorAll('[data-price-range]').forEach((priceRange) => {
      const priceInputs = priceRange.querySelectorAll('input[type="number"], input[type="text"]');
      if (Array.from(priceInputs).some((input) => input.value.trim() !== '')) activeCount += 1;
    });
    let badge = filterBtn.querySelector('.bottomsheet-trigger__badge');
    if (activeCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'bottomsheet-trigger__badge';
        filterBtn.appendChild(badge);
      }
      badge.textContent = activeCount;
      badge.classList.remove('hidden');
    } else if (badge) {
      badge.textContent = '0';
      badge.classList.add('hidden');
    }
    const applyBtn = document.querySelector('[data-bottomsheet-apply="filter-sort"]');
    if (applyBtn?.dataset.defaultLabel && !applyBtn.disabled) {
      applyBtn.textContent = applyBtn.dataset.defaultLabel;
    }
  }

  static updateURLHash(searchParams) {
    FacetFiltersForm.historyIndex += 1;
    history.pushState(
      { ...(history.state || {}), searchParams, facetHistoryIndex: FacetFiltersForm.historyIndex },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const currentFormId = event.target.closest('form').id;
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else if (currentFormId === 'FacetSortFormMobile' || currentFormId === 'FacetFiltersFormMobile') {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetFiltersFormMobile' || form.id === 'FacetSortFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.committedSearchParams = window.location.search.slice(1);
FacetFiltersForm.failedRequest = null;
FacetFiltersForm.activeRequest = null;
FacetFiltersForm.requestId = 0;
FacetFiltersForm.historyIndex = Number.isInteger(history.state?.facetHistoryIndex)
  ? history.state.facetHistoryIndex
  : 0;
FacetFiltersForm.restoringHistory = false;
history.replaceState(
  {
    ...(history.state || {}),
    searchParams: FacetFiltersForm.committedSearchParams,
    facetHistoryIndex: FacetFiltersForm.historyIndex,
  },
  '',
  window.location.href
);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener('collection:reloaded', () => FacetFiltersForm.syncVisibleProductCount());
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.syncVisibleProductCount());
} else {
  FacetFiltersForm.syncVisibleProductCount();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input:not([type="range"])').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input:not([type="range"])');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = this.parseValue(input.value);
    const min = this.parseValue(input.getAttribute('data-min'));
    const max = this.parseValue(input.getAttribute('data-max'));

    if (value === null) return;
    if (min !== null && value < min) input.value = min;
    if (max !== null && value > max) input.value = max;
  }

  parseValue(value) {
    const normalized = String(value ?? '').trim().replace(/\s/g, '');
    if (!normalized) return null;
    const commaIndex = normalized.lastIndexOf(',');
    const dotIndex = normalized.lastIndexOf('.');
    let numeric = normalized;
    if (commaIndex >= 0 && dotIndex >= 0) {
      const decimalIndex = Math.max(commaIndex, dotIndex);
      numeric = normalized.slice(0, decimalIndex).replace(/[.,]/g, '') + '.' + normalized.slice(decimalIndex + 1);
    } else if (commaIndex >= 0) {
      numeric = normalized.replace(/\./g, '').replace(',', '.');
    } else if (dotIndex >= 0 && normalized.length - dotIndex - 1 === 3) {
      numeric = normalized.replace(/\./g, '');
    }
    const parsed = Number(numeric);
    return Number.isFinite(parsed) ? parsed : null;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* =============================================
   Bottom Sheet Controller
   ============================================= */
class BottomSheetController {
  constructor() {
    this.activeSheetId = null;
    this.startY = 0;
    this.currentY = 0;
    this.isDragging = false;
    this.dragSheet = null;
    this.lastTrigger = null;
    this.formSnapshot = null;
    this.restoreOnClose = true;
    this.dragFrame = null;
    this.pendingDragY = 0;
    this.badgeFrame = null;

    this.relocateWrapper();

    document.body.classList.remove('bottomsheet-open');
    document.body.style.overflow = '';
    document.querySelectorAll('.bottomsheet').forEach((s) => {
      s.classList.remove('is-open');
      s.style.transform = '';
      s.setAttribute('aria-hidden', 'true');
      s.inert = true;
    });
    const backdrop = document.querySelector('[data-bottomsheet-backdrop]');
    if (backdrop) backdrop.classList.remove('is-visible');

    this.bindAll();
  }

  relocateWrapper() {
    // Move the fixed trigger, bottom sheets and backdrop to body so transformed/animated
    // collection containers cannot clip them and they survive ProductGridContainer re-renders.
    let portal = document.getElementById('bottomsheet-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'bottomsheet-portal';
      document.body.appendChild(portal);
    }

    // Find sheets that came in via the new page render (still inside grid container)
    const incomingSheets = document.querySelectorAll(
      '.mobile-facets__wrapper--bs .patirti-filter-trigger, .mobile-facets__wrapper--bs .bottomsheet, .mobile-facets__wrapper--bs [data-bottomsheet-backdrop]'
    );

    if (incomingSheets.length > 0) {
      if (this.activeSheet) this.close(this.activeSheet);
      portal.innerHTML = '';
      incomingSheets.forEach((el) => {
        portal.appendChild(el);
      });
    }
    // If no incoming sheets, keep existing portal contents (already in body, working)
  }

  get backdrop() {
    return document.querySelector('[data-bottomsheet-backdrop]');
  }

  get activeSheet() {
    if (!this.activeSheetId) return null;
    return document.querySelector(`[data-bottomsheet="${this.activeSheetId}"]`);
  }

  bindAll() {
    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('change', this.onDocumentChange.bind(this));
    document.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
    document.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    document.addEventListener('input', this.onDocumentInput.bind(this));
    document.addEventListener('keydown', this.onDocumentKeydown.bind(this));
  }

  onDocumentClick(e) {
    const trigger = e.target.closest('[data-bottomsheet-target]');
    if (trigger) {
      e.preventDefault();
      const target = trigger.getAttribute('data-bottomsheet-target');
      const sheet = document.querySelector(`[data-bottomsheet="${target}"]`);
      if (sheet) {
        this.lastTrigger = trigger;
        this.open(sheet);
      }
      return;
    }

    if (e.target.closest('[data-bottomsheet-close]')) {
      if (this.activeSheet) this.close(this.activeSheet);
      return;
    }

    if (e.target.closest('[data-bottomsheet-apply]')) {
      this.apply();
      return;
    }

    if (e.target.closest('[data-bottomsheet-clear]')) {
      this.clear();
      return;
    }

    if (e.target.closest('[data-bottomsheet-backdrop]')) {
      if (this.activeSheet) this.close(this.activeSheet);
      return;
    }
  }

  onDocumentChange(e) {
    const form = e.target.closest('#FacetFiltersFormMobile');
    if (!form) return;
    if (e.target.matches('.bottomsheet__sort-radio[name="sort_by"]')) {
      this.syncSortSelection(form, e.target);
      this.scheduleBadgeUpdate();
      return;
    }
    const option = e.target.closest('.bottomsheet__sort-option, .bottomsheet__checkbox-label, .bottomsheet__swatch-option');
    if (option) option.classList.toggle('active', Boolean(e.target.checked));
    else this.updateSelectionStyles(form);
    this.scheduleBadgeUpdate();
  }

  onDocumentInput(e) {
    const range = e.target.closest('[data-price-min], [data-price-max]');
    const priceRange = e.target.closest('[data-price-range]');
    if (!priceRange) return;
    if (range) {
      this.syncPriceRange(priceRange, range);
    } else {
      this.syncSlidersFromFields(priceRange);
    }
    this.scheduleBadgeUpdate();
  }

  scheduleBadgeUpdate() {
    if (this.badgeFrame !== null) return;
    this.badgeFrame = requestAnimationFrame(() => {
      this.badgeFrame = null;
      FacetFiltersForm.updateBottomSheetBadge();
    });
  }

  getFocusable(container) {
    return Array.from(
      container.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.offsetParent !== null);
  }

  updateSelectionStyles(root = document) {
    root.querySelectorAll('.bottomsheet__sort-option, .bottomsheet__checkbox-label, .bottomsheet__swatch-option').forEach((option) => {
      const input = option.querySelector('input');
      if (input) option.classList.toggle('active', input.checked);
    });
  }

  syncSortSelection(form, preferredInput) {
    const radios = Array.from(form.querySelectorAll('.bottomsheet__sort-radio[name="sort_by"]'));
    if (!radios.length) return;
    let selected = preferredInput && preferredInput.checked ? preferredInput : radios.find((input) => input.checked);
    if (!selected) selected = radios[0];
    radios.forEach((input) => {
      const isSelected = input === selected;
      input.checked = isSelected;
      const option = input.closest('.bottomsheet__sort-option');
      if (option) option.classList.toggle('active', isSelected);
    });
  }

  clear() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    form.querySelectorAll('[data-price-range]').forEach((priceRange) => {
      priceRange.querySelectorAll('input[type="number"], input[type="text"]').forEach((input) => {
        input.value = '';
      });
      const min = priceRange.querySelector('[data-price-min]');
      const max = priceRange.querySelector('[data-price-max]');
      if (min) min.value = min.min || '0';
      if (max) max.value = max.max;
      this.paintPriceTrack(priceRange);
    });
    const defaultSort = form.dataset.defaultSort;
    const sortInput = (defaultSort && form.querySelector(`input[name="sort_by"][value="${CSS.escape(defaultSort)}"]`)) || form.querySelector('input[name="sort_by"]');
    if (sortInput) sortInput.checked = true;
    this.syncSortSelection(form, sortInput);
    this.updateSelectionStyles();
    FacetFiltersForm.updateBottomSheetBadge();
  }

  apply() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;
    this.syncSortSelection(form);
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const resultsScrollTarget = this.getResultsScrollTarget();
    this.restoreOnClose = false;
    this.close(this.activeSheet, { restoreScroll: false, restoreFocus: false });
    if (typeof resultsScrollTarget === 'number') window.scrollTo(0, resultsScrollTarget);
    FacetFiltersForm.renderPage(params.toString(), null);
  }

  getResultsScrollTarget() {
    const savedScrollY = typeof this.savedScrollY === 'number' ? this.savedScrollY : window.scrollY;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return savedScrollY;
    const header = document.querySelector('.shopify-section-header-sticky, .ty-header-wrapper');
    const headerOffset = header ? header.getBoundingClientRect().height : 0;
    const documentTop = container.getBoundingClientRect().top + savedScrollY;
    return Math.max(0, Math.round(documentTop - headerOffset - 12));
  }

  captureFormState() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return null;
    return Array.from(form.elements).map((input) => ({ input, value: input.value, checked: input.checked }));
  }

  restoreFormState() {
    if (!this.formSnapshot) return;
    this.formSnapshot.forEach(({ input, value, checked }) => {
      if (!document.contains(input)) return;
      input.value = value;
      if ('checked' in input) input.checked = checked;
    });
    const root = this.activeSheet || document;
    this.updateSelectionStyles(root);
    root.querySelectorAll('[data-price-range]').forEach((priceRange) => this.paintPriceTrack(priceRange));
    FacetFiltersForm.updateBottomSheetBadge();
  }

  syncPriceRange(priceRange, changedRange) {
    if (!priceRange) return;
    const minRange = priceRange.querySelector('[data-price-min]');
    const maxRange = priceRange.querySelector('[data-price-max]');
    const fields = priceRange.querySelectorAll('input[type="number"], input[type="text"]');
    if (!minRange || !maxRange || fields.length < 2) return;
    if (Number(minRange.value) > Number(maxRange.value)) {
      if (changedRange === minRange) maxRange.value = minRange.value;
      else minRange.value = maxRange.value;
    }
    fields[0].value = minRange.value === minRange.min ? '' : minRange.value;
    fields[1].value = maxRange.value === maxRange.max ? '' : maxRange.value;
    if (typeof priceRange.setMinAndMaxValues === 'function') priceRange.setMinAndMaxValues();
    this.paintPriceTrack(priceRange);
  }

  syncSlidersFromFields(priceRange) {
    const minRange = priceRange.querySelector('[data-price-min]');
    const maxRange = priceRange.querySelector('[data-price-max]');
    const fields = priceRange.querySelectorAll('input[type="number"], input[type="text"]');
    if (!minRange || !maxRange || fields.length < 2) return;
    const minValue = this.parsePriceValue(fields[0].value);
    const maxValue = this.parsePriceValue(fields[1].value);
    minRange.value = minValue === null ? minRange.min : String(minValue);
    maxRange.value = maxValue === null ? maxRange.max : String(maxValue);
    this.paintPriceTrack(priceRange);
  }

  parsePriceValue(value) {
    const parser = document.querySelector('price-range');
    if (parser && typeof parser.parseValue === 'function') return parser.parseValue(value);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  paintPriceTrack(priceRange) {
    const minRange = priceRange.querySelector('[data-price-min]');
    const maxRange = priceRange.querySelector('[data-price-max]');
    if (!minRange || !maxRange) return;
    const maximum = Number(maxRange.max) || 1;
    priceRange.style.setProperty('--price-min', `${(Number(minRange.value) / maximum) * 100}%`);
    priceRange.style.setProperty('--price-max', `${(Number(maxRange.value) / maximum) * 100}%`);
  }

  onDocumentKeydown(e) {
    if (!this.activeSheet) return;
    if (document.querySelector('.tm-size-guide--open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close(this.activeSheet);
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = this.getFocusable(this.activeSheet);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  onTouchStart(e) {
    const handle = e.target.closest('[data-bottomsheet-handle]');
    if (!handle) return;
    this.dragSheet = handle.closest('.bottomsheet');
    if (!this.dragSheet) return;
    this.startY = e.touches[0].clientY;
    this.currentY = this.startY;
    this.isDragging = true;
    this.dragSheet.classList.add('is-dragging');
  }

  onTouchMove(e) {
    if (!this.isDragging || !this.dragSheet) return;
    this.currentY = e.touches[0].clientY;
    this.pendingDragY = Math.max(0, this.currentY - this.startY);
    if (this.dragFrame !== null) return;
    this.dragFrame = requestAnimationFrame(() => {
      this.dragFrame = null;
      if (this.isDragging && this.dragSheet) {
        this.dragSheet.style.transform = `translate3d(0, ${this.pendingDragY}px, 0)`;
      }
    });
  }

  onTouchEnd() {
    if (!this.isDragging || !this.dragSheet) return;
    this.isDragging = false;
    if (this.dragFrame !== null) cancelAnimationFrame(this.dragFrame);
    this.dragFrame = null;
    this.dragSheet.classList.remove('is-dragging');
    const deltaY = this.currentY - this.startY;
    this.dragSheet.style.transform = '';
    if (deltaY > 80) {
      this.close(this.dragSheet);
    }
    this.startY = 0;
    this.currentY = 0;
    this.pendingDragY = 0;
    this.dragSheet = null;
  }

  open(sheet) {
    document.querySelectorAll('.bottomsheet.is-open').forEach((s) => {
      if (s !== sheet) {
        s.classList.remove('is-open');
        s.setAttribute('aria-hidden', 'true');
        s.inert = true;
      }
    });
    const sheetId = sheet.dataset.bottomsheet;
    this.activeSheetId = sheetId;
    const form = sheet.querySelector('#FacetFiltersFormMobile');
    if (form) this.syncSortSelection(form);
    this.updateSelectionStyles(sheet);
    this.formSnapshot = this.captureFormState();
    this.restoreOnClose = true;
    sheet.setAttribute('aria-hidden', 'false');
    sheet.inert = false;
    this.savedScrollY = window.scrollY;
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('bottomsheet-open');
    const backdrop = this.backdrop;
    if (backdrop) backdrop.classList.add('is-visible');
    sheet.classList.add('is-open');
    const trigger = document.querySelector(`[data-bottomsheet-target="${sheetId}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    sheet.querySelectorAll('[data-price-range]').forEach((priceRange) => this.paintPriceTrack(priceRange));
    requestAnimationFrame(() => {
      const focusable = this.getFocusable(sheet);
      if (focusable.length) focusable[0].focus();
    });
  }

  close(sheet, options = {}) {
    const restoreScroll = options.restoreScroll !== false;
    const restoreFocus = options.restoreFocus !== false;
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      const focusedSheet = document.activeElement.closest('.bottomsheet');
      if (focusedSheet) document.activeElement.blur();
    }
    const targetSheet = sheet || this.activeSheet;
    if (targetSheet && this.restoreOnClose) this.restoreFormState();
    if (targetSheet) {
      targetSheet.classList.remove('is-open');
      targetSheet.setAttribute('aria-hidden', 'true');
      targetSheet.inert = true;
      targetSheet.style.transform = '';
    }
    document.querySelectorAll('.bottomsheet.is-open').forEach((s) => {
      s.classList.remove('is-open');
      s.setAttribute('aria-hidden', 'true');
      s.inert = true;
      s.style.transform = '';
    });
    const backdrop = this.backdrop;
    if (backdrop) backdrop.classList.remove('is-visible');
    document.body.classList.remove('bottomsheet-open');
    document.body.style.overflow = '';
    document.body.style.top = '';
    if (restoreScroll && typeof this.savedScrollY === 'number') {
      window.scrollTo(0, this.savedScrollY);
    }
    this.savedScrollY = null;
    if (this.activeSheetId) {
      const trigger = document.querySelector(`[data-bottomsheet-target="${this.activeSheetId}"]`);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    this.activeSheetId = null;
    this.formSnapshot = null;
    this.restoreOnClose = true;
    if (restoreFocus && this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    this.lastTrigger = null;
  }
}

function initBottomSheetController() {
  if (window.__bottomSheetController) return;
  window.__bottomSheetController = new BottomSheetController();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBottomSheetController);
} else {
  initBottomSheetController();
}
